import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Injector, Component, ViewEncapsulation, Inject, Input, ViewChild, Output, HostListener, Directive, NgModule } from '@angular/core';
import * as i5 from '@angular/forms';
import { NG_VALUE_ACCESSOR, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i1 from '@angular/material/core';
import { MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DomPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { UxTimepickerComponent, UxTimepickerComponentModule } from '@eui/components/legacy/ux-timepicker';
import * as _rollupMoment from 'moment';
import _rollupMoment__default from 'moment';
import * as i3 from '@eui/core';
import { DYNAMIC_COMPONENT_CONFIG } from '@eui/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i2 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i6 from '@angular/material/datepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import * as i7 from '@angular/material/input';
import { MatInputModule } from '@angular/material/input';
import * as i8 from '@eui/components/legacy/ux-button';
import { UxButtonComponentModule } from '@eui/components/legacy/ux-button';
import { MatMomentDateModule, MomentDateAdapter } from '@angular/material-moment-adapter';

const moment = _rollupMoment__default || _rollupMoment;
const UX_DATEPICKER_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => UxDatepickerComponent),
    multi: true,
};
class UxDatepickerComponent {
    constructor(adapter, translateService, uxAppShellService, componentFactoryResolver, injector, appRef, format) {
        this.adapter = adapter;
        this.translateService = translateService;
        this.uxAppShellService = uxAppShellService;
        this.componentFactoryResolver = componentFactoryResolver;
        this.injector = injector;
        this.appRef = appRef;
        this.styleClass = '';
        this.togglerIcon = 'eui-icon-ecl-calendar';
        this.type = 'regular';
        this.e2eAttr = 'ux-datepicker';
        this.datepickerFilter = this.datepickerFiltering;
        this._isDatetimepicker = false;
        this._noButton = false;
        this._isReadOnly = false;
        this._isDisabled = false;
        this._disableInput = false;
        this._disableButton = false;
        this._disablePicker = this.disableInput;
        this._longDateFormat = false;
        this._isClearable = false;
        this.inputWidthClass = '';
        this.onInputChange = new EventEmitter();
        this.onDateSelect = new EventEmitter();
        this.input = new FormControl();
        this.typeToStartView = {
            'year': 'multi-year',
            'month': 'year',
            'regular': 'month',
        };
        this.breakpointsValue = {
            isMobile: false,
            isTablet: false,
            isLtDesktop: false,
            isDesktop: false,
            isXL: false,
            isXXL: false,
        };
        this.responsive = false;
        this.destroy$ = new Subject();
        this.propagateChange = (_) => {
        };
        this.propagateTouched = () => {
        };
        this.format = format;
    }
    get isDatetimepicker() { return this._isDatetimepicker; }
    set isDatetimepicker(value) { this._isDatetimepicker = coerceBooleanProperty(value); }
    get noButton() { return this._noButton; }
    set noButton(value) { this._noButton = coerceBooleanProperty(value); }
    get isReadOnly() { return this._isReadOnly; }
    set isReadOnly(value) { this._isReadOnly = coerceBooleanProperty(value); }
    get isDisabled() { return this._isDisabled; }
    set isDisabled(value) { this._isDisabled = coerceBooleanProperty(value); }
    get disableInput() { return this._disableInput; }
    set disableInput(value) { this._disableInput = coerceBooleanProperty(value); }
    get disableButton() { return this._disableButton; }
    set disableButton(value) { this._disableButton = coerceBooleanProperty(value); }
    get disablePicker() { return this._disablePicker; }
    set disablePicker(value) { this._disablePicker = coerceBooleanProperty(value); }
    get longDateFormat() { return this._longDateFormat; }
    set longDateFormat(value) { this._longDateFormat = coerceBooleanProperty(value); }
    get isClearable() { return this._isClearable; }
    set isClearable(value) { this._isClearable = coerceBooleanProperty(value); }
    get restrictToRegex() { return this._restrictToRegex; }
    set restrictToRegex(value) {
        try {
            if (value instanceof RegExp) {
                this._restrictToRegex = value;
            }
            else if (typeof value === 'string') {
                this._restrictToRegex = new RegExp(value);
            }
            else {
                throw new Error(`restrictToRegex can only be string or RegExp, it cannot be ${typeof value}`);
            }
        }
        catch (e) {
            console.error(e);
        }
    }
    ngOnInit() {
        this.input = new FormControl({
            value: this.value,
            disabled: this.disableInput,
        });
        if (this.translateService.currentLang) {
            this.adapter.setLocale(this.translateService.currentLang);
            this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe((event) => {
                this.adapter.setLocale(event.lang);
            });
        }
        if (!this.placeholder) {
            if (this.type === 'regular' && !this.isDatetimepicker) {
                this.translateService.stream('eui.datepicker.PLACEHOLDER').pipe(takeUntil(this.destroy$)).subscribe((result) => {
                    this.placeholder = result;
                });
            }
            else if (this.type === 'regular' && this.isDatetimepicker) {
                this.translateService.stream('eui.datepicker.ISDATETIMEPICKER')
                    .pipe(takeUntil(this.destroy$)).subscribe((result) => {
                    this.placeholder = result;
                });
            }
            else if (this.type === 'year') {
                this.translateService.stream('eui.datepicker.YEAR-PLACEHOLDER')
                    .pipe(takeUntil(this.destroy$)).subscribe((result) => {
                    this.placeholder = result;
                });
            }
            else if (this.type === 'month') {
                this.translateService.stream('eui.datepicker.MONTH-PLACEHOLDER')
                    .pipe(takeUntil(this.destroy$)).subscribe((result) => {
                    this.placeholder = result;
                });
            }
        }
        this.uxAppShellService.breakpoints$.pipe(takeUntil(this.destroy$)).subscribe(bkps => this.breakpointsValue = bkps);
        if (this.isDatetimepicker) {
            this.uxdatepicker['closeBkp'] = this.uxdatepicker.close;
            this.uxdatepicker.close = () => {
                return false;
            };
        }
    }
    ngAfterContentInit() {
        if (this.isDatetimepicker) {
            this.styleClass += ' ux-datepicker--datetime';
        }
        if (this.longDateFormat) {
            this.styleClass += ' ux-datepicker--long-date-format';
        }
        if (this.inputWidthClass && this.inputWidthClass.substring(0, 3) === 'col') {
            this.responsive = true; // adds responsive class name to input
        }
    }
    ngOnChanges(changes) {
        if (changes && changes['isReadOnly']) {
            if (this.isReadOnly) {
                this.noButton = true;
                this.disableInput = true;
                this.placeholder = null;
            }
            else {
                this.input.enable();
                this.noButton = false;
                if (!this.placeholder) {
                    if (this.type === 'regular' && !this.isDatetimepicker) {
                        this.translateService.stream('eui.datepicker.PLACEHOLDER').pipe(takeUntil(this.destroy$)).subscribe((result) => {
                            this.placeholder = result;
                        });
                    }
                    else if (this.type === 'regular' && this.isDatetimepicker) {
                        this.translateService.stream('eui.datepicker.ISDATETIMEPICKER')
                            .pipe(takeUntil(this.destroy$)).subscribe((result) => {
                            this.placeholder = result;
                        });
                    }
                    else if (this.type === 'year') {
                        this.translateService.stream('eui.datepicker.YEAR-PLACEHOLDER')
                            .pipe(takeUntil(this.destroy$)).subscribe((result) => {
                            this.placeholder = result;
                        });
                    }
                    else if (this.type === 'month') {
                        this.translateService.stream('eui.datepicker.MONTH-PLACEHOLDER')
                            .pipe(takeUntil(this.destroy$)).subscribe((result) => {
                            this.placeholder = result;
                        });
                    }
                }
            }
        }
        if (changes && changes['isDisabled']) {
            this.setDisabledState(this.isDisabled);
        }
        if (changes && changes['value']) {
            this.input.patchValue(this.value);
        }
    }
    onClickOutside(e) {
        if (this.isDatetimepicker) {
            let clickInPopup = false;
            let clickOutside = false;
            this.getEventPath(e).forEach((p) => {
                if (p.className && p.className.indexOf('mat-datepicker-content') !== -1) {
                    clickInPopup = true;
                }
                if (p.className && p.className.indexOf('cdk-overlay-backdrop') !== -1) {
                    clickOutside = true;
                }
            });
            if (!clickInPopup && clickOutside) {
                this.uxdatepicker['closeBkp']();
            }
        }
    }
    createInjector(data) {
        const injectorTokens = [{ provide: DYNAMIC_COMPONENT_CONFIG, useValue: data }];
        return Injector.create({
            parent: this.injector,
            providers: injectorTokens
        });
    }
    openCalendar() {
        if (!this.isReadOnly) {
            this.uxdatepicker.open();
        }
        if (this.isDatetimepicker) {
            document.querySelector('.mat-datepicker-content .mat-calendar').classList.add('ux-datepicker--container-height-large');
            document.querySelector('.mat-calendar-content').classList.add('ux-datepicker--set-content-height');
            this.portalHost = new DomPortalOutlet(document.querySelector('mat-calendar'), this.componentFactoryResolver, this.appRef, this.injector);
            const timeConfig = {
                hour: this.value && this.value.getHours(),
                mins: this.value && this.value.getMinutes(),
                isDatetimepicker: this.isDatetimepicker,
                callbackFn: (hour, mins) => {
                    if (this.value) {
                        this.value.setHours(hour || 0);
                        this.value.setMinutes(mins || 0);
                        this.input.patchValue(this.value);
                        if (this.dateOutputFormat) {
                            this.value = moment(this.value, this.dateOutputFormat).toDate();
                            this.propagateChange(moment(this.value).format(this.dateOutputFormat));
                        }
                        else {
                            this.propagateChange(moment(this.value));
                        }
                    }
                }
            };
            this.portal = new ComponentPortal(UxTimepickerComponent, null, this.createInjector(timeConfig));
            const portalComponentInstance = this.portalHost.attach(this.portal);
        }
    }
    onClearClick() {
        this.value = '';
        this.input.patchValue('');
        this.propagateChange(this.value);
        this.onInputChange.emit(this.value);
        this.onDateSelect.emit(this.value);
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
        this.portalHost?.detach();
    }
    getEventPath(event) {
        if (event.path) {
            return event.path;
        }
        let path = [];
        let target = event.target;
        while (target.parentNode) {
            path.push(target);
            target = target.parentNode;
        }
        path.push(document, window);
        return path;
    }
    chosenDateHandler(normalizedDate, datepicker) {
        if (this.dateOutputFormat) {
            let formattedValue;
            formattedValue = moment(normalizedDate, this.dateOutputFormat);
            this.input.setValue(formattedValue);
            this.propagateChange(formattedValue.format(this.dateOutputFormat));
            this.onInputChange.emit(formattedValue.format(this.dateOutputFormat));
            this.onDateSelect.emit(formattedValue.format(this.dateOutputFormat));
        }
        else {
            this.value = normalizedDate;
            this.input.setValue(this.value);
            this.propagateChange(this.value);
            this.onDateSelect.emit(this.value ? this.value.toISOString() : null);
            this.onInputChange.emit(this.value ? this.value.toISOString() : null);
        }
        datepicker.close();
    }
    datepickerFiltering() {
        return true;
    }
    inputChange(e) {
        if (!this.isDatetimepicker) {
            if (e.value === null) {
                this.propagateChange(null);
                this.onInputChange.emit(null);
            }
            else if (this.dateOutputFormat) {
                this.value = moment(e.value, this.dateOutputFormat);
                this.propagateChange(this.value.format(this.dateOutputFormat));
                this.onInputChange.emit(this.value.format(this.dateOutputFormat));
            }
            else {
                this.propagateChange(e.value);
                this.onInputChange.emit(e && e.value ? e.value.toISOString() : null);
            }
        }
    }
    dateSelect(e) {
        if (this.isDatetimepicker) {
            const hour = this.value && this.value.getHours();
            const mins = this.value && this.value.getMinutes();
            this.value = moment(e.value, this.format.parse.dateInput).toDate();
            this.value.setHours(hour || 0);
            this.value.setMinutes(mins || 0);
            if (this.uxdatepicker.opened) {
                this.input.patchValue(this.value);
            }
            if (this.dateOutputFormat && this.value != null) {
                this.propagateChange(moment(this.value).format(this.dateOutputFormat));
            }
            else {
                this.propagateChange(moment(this.value));
            }
        }
        if (this.dateOutputFormat) {
            this.value = moment(e.value, this.dateOutputFormat).toDate();
            this.onDateSelect.emit(moment(this.value).format(this.dateOutputFormat));
        }
        else {
            this.onDateSelect.emit(e && e.value ? e.value.toISOString() : null);
        }
    }
    changedInput(e) {
        const value = (typeof e === 'string') ? e : e.target.value;
        this.value = moment(value, this.format.parse.dateInput).toDate();
        this.input.patchValue(this.value);
        if (this.dateOutputFormat && this.value != null) {
            this.value = moment(value, this.dateOutputFormat).toDate();
            this.propagateChange(moment(this.value).format(this.dateOutputFormat));
        }
        else {
            this.propagateChange(moment(this.value));
        }
    }
    onKeypress(e) {
        if (e.key === 'Enter') {
            this.openCalendar();
            e.preventDefault();
        }
        if (this._restrictToRegex) {
            if (!this._restrictToRegex.test(e.key)) {
                e.preventDefault();
            }
        }
    }
    writeValue(value) {
        this.value = value || '';
        this.input.patchValue(value);
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    convTypeToStartView(type) {
        switch (type) {
            case 'month':
                return 'year';
            case 'year':
                return 'multi-year';
            case 'regular':
                return 'month';
        }
    }
    setDisabledState(isDisabled) {
        if (isDisabled) {
            this.disableInput = this.disablePicker = this.disableButton = true;
            this.input.disable();
        }
        else {
            this.input.enable();
            this.disablePicker = this.disableButton = false;
        }
    }
}
/** @nocollapse */ UxDatepickerComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDatepickerComponent, deps: [{ token: i1.DateAdapter }, { token: i2.TranslateService }, { token: i3.UxAppShellService }, { token: i0.ComponentFactoryResolver }, { token: i0.Injector }, { token: i0.ApplicationRef }, { token: MAT_DATE_FORMATS }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ UxDatepickerComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.0.3", type: UxDatepickerComponent, selector: "ux-datepicker", inputs: { styleClass: "styleClass", value: "value", togglerIcon: "togglerIcon", togglerLabel: "togglerLabel", placeholder: "placeholder", type: "type", startView: "startView", e2eAttr: "e2eAttr", minDate: "minDate", maxDate: "maxDate", datepickerFilter: "datepickerFilter", isDatetimepicker: "isDatetimepicker", noButton: "noButton", isReadOnly: "isReadOnly", isDisabled: "isDisabled", disableInput: "disableInput", disableButton: "disableButton", disablePicker: "disablePicker", dateOutputFormat: "dateOutputFormat", longDateFormat: "longDateFormat", isClearable: "isClearable", inputWidthClass: "inputWidthClass", restrictToRegex: "restrictToRegex" }, outputs: { onInputChange: "onInputChange", onDateSelect: "onDateSelect" }, host: { listeners: { "body:click": "onClickOutside($event)" } }, providers: [
        UX_DATEPICKER_ACCESSOR,
    ], viewQueries: [{ propertyName: "uxdatepicker", first: true, predicate: ["uxdatepicker"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<div class=\"ux-datepicker ux-datepicker-{{ type }} {{ styleClass }}\" [class.ux-datepicker--responsive]=\"responsive\" attr.data-e2e=\"{{ e2eAttr }}\">\n    <div class=\"ux-datepicker__date-wrapper\" [ngClass]=\"inputWidthClass\">\n        <div class=\"ux-datepicker__input-wrapper\" [class.ux-datepicker__clear-date-wrapper]=\"isClearable\">\n            <input\n                [class.ux-datepicker--read-only]=\"isReadOnly\"\n                [class.ux-datepicker--disabled]=\"isDisabled\"\n                [class.ux-datepicker--clearable]=\"isClearable && !longDateFormat && !isDatetimepicker\"\n                [class.ux-datepicker--no-button]=\"noButton\"\n                [ngClass]=\"!responsive ? inputWidthClass : ''\"\n                type=\"text\"\n                matInput\n                [formControl]=\"input\"\n                [matDatepicker]=\"uxdatepicker\"\n                placeholder=\"{{ placeholder }}\"\n                (dateInput)=\"inputChange($event)\"\n                (dateChange)=\"dateSelect($event)\"\n                [min]=\"minDate\"\n                [max]=\"maxDate\"\n                [matDatepickerFilter]=\"datepickerFilter\"\n                (focus)=\"openCalendar()\"\n                (click)=\"openCalendar()\"\n                (keypress)=\"onKeypress($event)\"\n                (change)=\" isDatetimepicker ? changedInput($event) : null\">\n            <span class=\"ux-clearable-input-wrapper__clear\" *ngIf=\"isClearable && input.value && !input.disabled && !isReadOnly\" (click)=\"onClearClick()\"></span>\n        </div>\n        <mat-datepicker\n            #uxdatepicker\n            panelClass=\"mat-calendar-{{ type }}\"\n            [startView]=\"(type==='regular') ? (startView ? startView : convTypeToStartView(type)) : convTypeToStartView(type)\"\n            (yearSelected)=\"type === 'year' ? chosenDateHandler($event, uxdatepicker) : null\"\n            (monthSelected)=\"type === 'month' ? chosenDateHandler($event, uxdatepicker) : null\"\n            [touchUi]=\"breakpointsValue.isTablet || breakpointsValue.isMobile\"\n            [disabled]=\"disablePicker\">\n        </mat-datepicker>\n        <ux-button *ngIf=\"!noButton\"\n               [isSecondary]=\"true\"\n               (click)=\"openCalendar()\"\n               styleClass=\"ux-datepicker__toggler ux-datepicker__toggler-{{ !togglerLabel ? 'icon' : 'label' }}\"\n               [isDisabled]=\"disableButton\">\n            <span *ngIf=\"!togglerLabel\" aria-label=\"Calendar Icon\" role=\"img\" class=\"ux-button__icon eui-icon eui-icon-fw {{ togglerIcon }}\"></span>\n            <span *ngIf=\"togglerLabel\">{{ togglerLabel }}</span>\n        </ux-button>\n    </div>\n</div>\n", styles: ["ux-datepicker{display:flex;width:100%}ux-datepicker.ng-invalid.ng-touched .mat-mdc-input-element{border:1px solid var(--eui-base-color-danger-100)}ux-datepicker.ng-invalid.ng-untouched .mat-mdc-input-element,ux-datepicker.ng-valid.ng-touched .mat-mdc-input-element{border:1px solid var(--eui-base-color-grey-20)}ux-datepicker.ng-invalid.ng-untouched .ux-datepicker__toggler,ux-datepicker.ng-valid.ng-touched .ux-datepicker__toggler{background-color:var(--eui-base-color-grey-10)!important;border:1px solid var(--eui-base-color-grey-20)!important;border-left:none!important;color:inherit!important}.ux-datepicker{display:inline-flex;flex:1 1 100%;position:relative}.ux-datepicker__date-wrapper{display:flex;padding:0}.ux-datepicker__input-wrapper{display:flex;width:100%}.ux-datepicker input{border-bottom-left-radius:var(--eui-base-border-radius);border-top-left-radius:var(--eui-base-border-radius);border-bottom-right-radius:0;border-top-right-radius:0;width:calc(8 * var(--eui-base-spacing-m))}.ux-datepicker input.mat-mdc-input-element{border:1px solid var(--eui-base-color-grey-20);padding:var(--eui-base-spacing-xs) var(--eui-base-spacing-s)}.ux-datepicker input.mat-mdc-input-element:focus{border:1px solid var(--eui-base-color-primary-75)}.ux-datepicker input.ux-datepicker--no-button{border-bottom-right-radius:var(--eui-base-border-radius);border-top-right-radius:var(--eui-base-border-radius)}.ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-grey-10);border:1px solid var(--eui-base-color-grey-20);border-bottom-left-radius:0;border-left:0 none;border-top-left-radius:0;box-shadow:none;height:calc(var(--eui-base-spacing-2xl) + 2px);opacity:1}.ux-form-group--danger .ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-danger-10);border:1px solid var(--eui-base-color-danger-100);border-left:none;color:var(--eui-base-color-danger-100)}.ux-form-group--success .ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-success-10);border:1px solid var(--eui-base-color-success-100);border-left:none;color:var(--eui-base-color-success-100)}.ux-form-group--warning .ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-warning-10);border:1px solid var(--eui-base-color-warning-100);border-left:none;color:var(--eui-base-color-warning-100)}.ux-datepicker button.ux-datepicker__toggler:enabled:hover{border-left:0 none}.ux-datepicker button.ux-datepicker__toggler-icon{font-size:var(--eui-base-font-size);padding:0}.ux-datepicker button.ux-datepicker__toggler-label{width:auto}.eui-t-compact .ux-datepicker button.ux-datepicker__toggler.ux-button--icon-only{min-width:calc(2.5 * var(--eui-base-spacing-m))}.ux-datepicker--container-height-large{height:auto!important}.ux-datepicker--set-content-height{height:auto}.ux-datepicker--long-date-format input{width:calc(12.5 * var(--eui-base-spacing-m))}.ux-datepicker--datetime input{width:calc(11 * var(--eui-base-spacing-m))}.ux-datepicker__clear-date-wrapper{display:flex;flex:1 1 0%;position:relative;flex:0 1 auto}.ux-datepicker__clear-date-wrapper .ux-clearable-input-wrapper__clear{color:var(--eui-base-color-grey-35);cursor:pointer;font-size:var(--eui-base-font-size-xl);font-style:normal;position:absolute;right:var(--eui-base-spacing-xs);top:var(--eui-base-spacing-xs);-webkit-user-select:none;user-select:none;z-index:var(--eui-base-z-index-clearable-icon)}.ux-datepicker__clear-date-wrapper .ux-clearable-input-wrapper__clear:hover{color:var(--eui-base-color-danger);opacity:1}.ux-datepicker__clear-date-wrapper .ux-clearable-input-wrapper__clear:before{font-family:eui-icons!important}.ux-datepicker--responsive input{width:100%}.ux-datepicker--read-only{background-color:transparent!important;border:none!important;color:var(--eui-base-color-grey-90)!important;cursor:not-allowed!important;height:inherit;padding:var(--eui-base-spacing-2xs) 0!important;pointer-events:none;font-style:normal;font-weight:700}.ux-form-group--compact .ux-datepicker--read-only{height:calc(var(--eui-base-spacing-m) + var(--eui-base-spacing-s))}.ux-datepicker--disabled{background-image:none;box-shadow:none;cursor:not-allowed;opacity:var(--eui-base-disabled-opacity)}\n"], dependencies: [{ kind: "directive", type: i4.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i5.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i5.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i5.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "component", type: i6.MatDatepicker, selector: "mat-datepicker", exportAs: ["matDatepicker"] }, { kind: "directive", type: i6.MatDatepickerInput, selector: "input[matDatepicker]", inputs: ["matDatepicker", "min", "max", "matDatepickerFilter"], exportAs: ["matDatepickerInput"] }, { kind: "directive", type: i7.MatInput, selector: "input[matInput], textarea[matInput], select[matNativeControl],      input[matNativeControl], textarea[matNativeControl]", inputs: ["disabled", "id", "placeholder", "name", "required", "type", "errorStateMatcher", "aria-describedby", "value", "readonly"], exportAs: ["matInput"] }, { kind: "component", type: i8.UxButtonComponent, selector: "ux-button", inputs: ["type", "styleClass", "typeClass", "iconClass", "isOutline", "isSecondary", "isSmall", "isLarge", "isBlock", "isFlat", "isLineWrap", "tabindex", "ariaLabel", "isDisabled", "e2eAttr"] }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDatepickerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ux-datepicker', providers: [
                        UX_DATEPICKER_ACCESSOR,
                    ], encapsulation: ViewEncapsulation.None, template: "<div class=\"ux-datepicker ux-datepicker-{{ type }} {{ styleClass }}\" [class.ux-datepicker--responsive]=\"responsive\" attr.data-e2e=\"{{ e2eAttr }}\">\n    <div class=\"ux-datepicker__date-wrapper\" [ngClass]=\"inputWidthClass\">\n        <div class=\"ux-datepicker__input-wrapper\" [class.ux-datepicker__clear-date-wrapper]=\"isClearable\">\n            <input\n                [class.ux-datepicker--read-only]=\"isReadOnly\"\n                [class.ux-datepicker--disabled]=\"isDisabled\"\n                [class.ux-datepicker--clearable]=\"isClearable && !longDateFormat && !isDatetimepicker\"\n                [class.ux-datepicker--no-button]=\"noButton\"\n                [ngClass]=\"!responsive ? inputWidthClass : ''\"\n                type=\"text\"\n                matInput\n                [formControl]=\"input\"\n                [matDatepicker]=\"uxdatepicker\"\n                placeholder=\"{{ placeholder }}\"\n                (dateInput)=\"inputChange($event)\"\n                (dateChange)=\"dateSelect($event)\"\n                [min]=\"minDate\"\n                [max]=\"maxDate\"\n                [matDatepickerFilter]=\"datepickerFilter\"\n                (focus)=\"openCalendar()\"\n                (click)=\"openCalendar()\"\n                (keypress)=\"onKeypress($event)\"\n                (change)=\" isDatetimepicker ? changedInput($event) : null\">\n            <span class=\"ux-clearable-input-wrapper__clear\" *ngIf=\"isClearable && input.value && !input.disabled && !isReadOnly\" (click)=\"onClearClick()\"></span>\n        </div>\n        <mat-datepicker\n            #uxdatepicker\n            panelClass=\"mat-calendar-{{ type }}\"\n            [startView]=\"(type==='regular') ? (startView ? startView : convTypeToStartView(type)) : convTypeToStartView(type)\"\n            (yearSelected)=\"type === 'year' ? chosenDateHandler($event, uxdatepicker) : null\"\n            (monthSelected)=\"type === 'month' ? chosenDateHandler($event, uxdatepicker) : null\"\n            [touchUi]=\"breakpointsValue.isTablet || breakpointsValue.isMobile\"\n            [disabled]=\"disablePicker\">\n        </mat-datepicker>\n        <ux-button *ngIf=\"!noButton\"\n               [isSecondary]=\"true\"\n               (click)=\"openCalendar()\"\n               styleClass=\"ux-datepicker__toggler ux-datepicker__toggler-{{ !togglerLabel ? 'icon' : 'label' }}\"\n               [isDisabled]=\"disableButton\">\n            <span *ngIf=\"!togglerLabel\" aria-label=\"Calendar Icon\" role=\"img\" class=\"ux-button__icon eui-icon eui-icon-fw {{ togglerIcon }}\"></span>\n            <span *ngIf=\"togglerLabel\">{{ togglerLabel }}</span>\n        </ux-button>\n    </div>\n</div>\n", styles: ["ux-datepicker{display:flex;width:100%}ux-datepicker.ng-invalid.ng-touched .mat-mdc-input-element{border:1px solid var(--eui-base-color-danger-100)}ux-datepicker.ng-invalid.ng-untouched .mat-mdc-input-element,ux-datepicker.ng-valid.ng-touched .mat-mdc-input-element{border:1px solid var(--eui-base-color-grey-20)}ux-datepicker.ng-invalid.ng-untouched .ux-datepicker__toggler,ux-datepicker.ng-valid.ng-touched .ux-datepicker__toggler{background-color:var(--eui-base-color-grey-10)!important;border:1px solid var(--eui-base-color-grey-20)!important;border-left:none!important;color:inherit!important}.ux-datepicker{display:inline-flex;flex:1 1 100%;position:relative}.ux-datepicker__date-wrapper{display:flex;padding:0}.ux-datepicker__input-wrapper{display:flex;width:100%}.ux-datepicker input{border-bottom-left-radius:var(--eui-base-border-radius);border-top-left-radius:var(--eui-base-border-radius);border-bottom-right-radius:0;border-top-right-radius:0;width:calc(8 * var(--eui-base-spacing-m))}.ux-datepicker input.mat-mdc-input-element{border:1px solid var(--eui-base-color-grey-20);padding:var(--eui-base-spacing-xs) var(--eui-base-spacing-s)}.ux-datepicker input.mat-mdc-input-element:focus{border:1px solid var(--eui-base-color-primary-75)}.ux-datepicker input.ux-datepicker--no-button{border-bottom-right-radius:var(--eui-base-border-radius);border-top-right-radius:var(--eui-base-border-radius)}.ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-grey-10);border:1px solid var(--eui-base-color-grey-20);border-bottom-left-radius:0;border-left:0 none;border-top-left-radius:0;box-shadow:none;height:calc(var(--eui-base-spacing-2xl) + 2px);opacity:1}.ux-form-group--danger .ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-danger-10);border:1px solid var(--eui-base-color-danger-100);border-left:none;color:var(--eui-base-color-danger-100)}.ux-form-group--success .ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-success-10);border:1px solid var(--eui-base-color-success-100);border-left:none;color:var(--eui-base-color-success-100)}.ux-form-group--warning .ux-datepicker button.ux-datepicker__toggler{background-color:var(--eui-base-color-warning-10);border:1px solid var(--eui-base-color-warning-100);border-left:none;color:var(--eui-base-color-warning-100)}.ux-datepicker button.ux-datepicker__toggler:enabled:hover{border-left:0 none}.ux-datepicker button.ux-datepicker__toggler-icon{font-size:var(--eui-base-font-size);padding:0}.ux-datepicker button.ux-datepicker__toggler-label{width:auto}.eui-t-compact .ux-datepicker button.ux-datepicker__toggler.ux-button--icon-only{min-width:calc(2.5 * var(--eui-base-spacing-m))}.ux-datepicker--container-height-large{height:auto!important}.ux-datepicker--set-content-height{height:auto}.ux-datepicker--long-date-format input{width:calc(12.5 * var(--eui-base-spacing-m))}.ux-datepicker--datetime input{width:calc(11 * var(--eui-base-spacing-m))}.ux-datepicker__clear-date-wrapper{display:flex;flex:1 1 0%;position:relative;flex:0 1 auto}.ux-datepicker__clear-date-wrapper .ux-clearable-input-wrapper__clear{color:var(--eui-base-color-grey-35);cursor:pointer;font-size:var(--eui-base-font-size-xl);font-style:normal;position:absolute;right:var(--eui-base-spacing-xs);top:var(--eui-base-spacing-xs);-webkit-user-select:none;user-select:none;z-index:var(--eui-base-z-index-clearable-icon)}.ux-datepicker__clear-date-wrapper .ux-clearable-input-wrapper__clear:hover{color:var(--eui-base-color-danger);opacity:1}.ux-datepicker__clear-date-wrapper .ux-clearable-input-wrapper__clear:before{font-family:eui-icons!important}.ux-datepicker--responsive input{width:100%}.ux-datepicker--read-only{background-color:transparent!important;border:none!important;color:var(--eui-base-color-grey-90)!important;cursor:not-allowed!important;height:inherit;padding:var(--eui-base-spacing-2xs) 0!important;pointer-events:none;font-style:normal;font-weight:700}.ux-form-group--compact .ux-datepicker--read-only{height:calc(var(--eui-base-spacing-m) + var(--eui-base-spacing-s))}.ux-datepicker--disabled{background-image:none;box-shadow:none;cursor:not-allowed;opacity:var(--eui-base-disabled-opacity)}\n"] }]
        }], ctorParameters: function () { return [{ type: i1.DateAdapter }, { type: i2.TranslateService }, { type: i3.UxAppShellService }, { type: i0.ComponentFactoryResolver }, { type: i0.Injector }, { type: i0.ApplicationRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_DATE_FORMATS]
                }] }]; }, propDecorators: { styleClass: [{
                type: Input
            }], value: [{
                type: Input
            }], togglerIcon: [{
                type: Input
            }], togglerLabel: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], type: [{
                type: Input
            }], startView: [{
                type: Input
            }], e2eAttr: [{
                type: Input
            }], minDate: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], datepickerFilter: [{
                type: Input
            }], isDatetimepicker: [{
                type: Input
            }], noButton: [{
                type: Input
            }], isReadOnly: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], disableInput: [{
                type: Input
            }], disableButton: [{
                type: Input
            }], disablePicker: [{
                type: Input
            }], dateOutputFormat: [{
                type: Input
            }], longDateFormat: [{
                type: Input
            }], isClearable: [{
                type: Input
            }], inputWidthClass: [{
                type: Input
            }], restrictToRegex: [{
                type: Input
            }], uxdatepicker: [{
                type: ViewChild,
                args: ['uxdatepicker', { static: true }]
            }], onInputChange: [{
                type: Output
            }], onDateSelect: [{
                type: Output
            }], onClickOutside: [{
                type: HostListener,
                args: ['body:click', ['$event']]
            }] } });

const LETTER_FORMAT = {
    parse: {
        dateInput: 'LL',
    },
    display: {
        dateInput: 'LL',
        monthYearLabel: 'LL',
    },
};
const YEAR_FORMAT = {
    parse: {
        dateInput: 'YYYY',
    },
    display: {
        dateInput: 'YYYY',
        monthYearLabel: 'YYYY',
        dateA11yLabel: 'YYYY',
        monthYearA11yLabel: 'YYYY',
    },
};
const MONTH_YEAR_FORMAT = {
    parse: {
        dateInput: 'MM/YYYY',
    },
    display: {
        dateInput: 'MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};
class UxLetterFormatDirective {
}
/** @nocollapse */ UxLetterFormatDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxLetterFormatDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ UxLetterFormatDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: UxLetterFormatDirective, selector: "[uxLetterFormat]", providers: [
        { provide: MAT_DATE_FORMATS, useValue: LETTER_FORMAT },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxLetterFormatDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uxLetterFormat]',
                    providers: [
                        { provide: MAT_DATE_FORMATS, useValue: LETTER_FORMAT },
                    ],
                }]
        }] });
class UxYearFormatDirective {
}
/** @nocollapse */ UxYearFormatDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxYearFormatDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ UxYearFormatDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: UxYearFormatDirective, selector: "[uxYearFormat]", providers: [
        { provide: MAT_DATE_FORMATS, useValue: YEAR_FORMAT },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxYearFormatDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uxYearFormat]',
                    providers: [
                        { provide: MAT_DATE_FORMATS, useValue: YEAR_FORMAT },
                    ],
                }]
        }] });
class UxMonthYearFormatDirective {
}
/** @nocollapse */ UxMonthYearFormatDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxMonthYearFormatDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ UxMonthYearFormatDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.0.3", type: UxMonthYearFormatDirective, selector: "[uxMonthYearFormat]", providers: [
        { provide: MAT_DATE_FORMATS, useValue: MONTH_YEAR_FORMAT },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxMonthYearFormatDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[uxMonthYearFormat]',
                    providers: [
                        { provide: MAT_DATE_FORMATS, useValue: MONTH_YEAR_FORMAT },
                    ],
                }]
        }] });
class UxDateFormatsModule {
}
/** @nocollapse */ UxDateFormatsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDateFormatsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ UxDateFormatsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: UxDateFormatsModule, declarations: [UxYearFormatDirective, UxLetterFormatDirective, UxMonthYearFormatDirective], imports: [CommonModule], exports: [UxYearFormatDirective, UxLetterFormatDirective, UxMonthYearFormatDirective] });
/** @nocollapse */ UxDateFormatsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDateFormatsModule, imports: [CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDateFormatsModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    exports: [UxYearFormatDirective, UxLetterFormatDirective, UxMonthYearFormatDirective],
                    declarations: [UxYearFormatDirective, UxLetterFormatDirective, UxMonthYearFormatDirective],
                }]
        }] });

const DEFAULT_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MM/YYYY',
        dateA11yLabel: 'Date/Month/Year',
        monthYearA11yLabel: 'Month/Year',
    },
};
class UxDatepickerComponentModule {
}
/** @nocollapse */ UxDatepickerComponentModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDatepickerComponentModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ UxDatepickerComponentModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.3", ngImport: i0, type: UxDatepickerComponentModule, declarations: [UxDatepickerComponent], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatInputModule,
        TranslateModule,
        MatMomentDateModule,
        UxButtonComponentModule,
        UxTimepickerComponentModule,
        UxDateFormatsModule], exports: [UxDatepickerComponent,
        UxDateFormatsModule] });
/** @nocollapse */ UxDatepickerComponentModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDatepickerComponentModule, providers: [
        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
        { provide: MAT_DATE_FORMATS, useValue: DEFAULT_FORMATS },
    ], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatInputModule,
        TranslateModule,
        MatMomentDateModule,
        UxButtonComponentModule,
        UxTimepickerComponentModule,
        UxDateFormatsModule, UxDateFormatsModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.3", ngImport: i0, type: UxDatepickerComponentModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        ReactiveFormsModule,
                        MatDatepickerModule,
                        MatInputModule,
                        TranslateModule,
                        MatMomentDateModule,
                        UxButtonComponentModule,
                        UxTimepickerComponentModule,
                        UxDateFormatsModule
                    ],
                    exports: [
                        UxDatepickerComponent,
                        UxDateFormatsModule
                    ],
                    declarations: [
                        UxDatepickerComponent
                    ],
                    entryComponents: [UxTimepickerComponent],
                    providers: [
                        { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
                        { provide: MAT_DATE_FORMATS, useValue: DEFAULT_FORMATS },
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_FORMATS, LETTER_FORMAT, MONTH_YEAR_FORMAT, UX_DATEPICKER_ACCESSOR, UxDateFormatsModule, UxDatepickerComponent, UxDatepickerComponentModule, UxLetterFormatDirective, UxMonthYearFormatDirective, UxYearFormatDirective, YEAR_FORMAT };
